import React, { useState, useEffect, useCallback } from "react";
import { Spinner, Form } from "react-bootstrap";
import { useNavigate, useLocation } from "react-router-dom";
import {
  ClipboardCheck, Plus, Upload, Pencil, Trash2, Copy, Eye, Send, Archive,
  Clock, Users, BarChart3, HelpCircle, MoreVertical, Store,
  RefreshCw, KeyRound, Play, Globe,
} from "lucide-react";
import apiClient from "../../utils/apiClient";
import { PageHeader, StatsCard, StatsGrid, EmptyState, SearchInput, ConfirmModal, AppButton, Paginator, ActionMenu, ActionMenuItem, ActionMenuDivider, AppModal } from "../ui";
import CommonToaster from "../Common/CommonToaster";
import { assessmentsTrail } from "../QuizBank/quizCenterTrail";
import "./Assessments.scss";

const MODE_LABELS = { practice: "Practice", exam: "Exam", sectioned_exam: "Sectioned" };
const STATUS_LABELS = { draft: "Draft", published: "Published", archived: "Archived" };
const PAGE_SIZE = 20;

// Human-readable price for a paid marketplace listing; null for free.
const fmtPrice = (p) => {
  if (!p || p.model !== "paid") return null;
  const sym = p.currency === "INR" ? "₹" : `${p.currency || ""} `;
  const per =
    p.period === "monthly" ? "/mo" :
    p.period === "yearly" ? "/yr" :
    p.period === "lifetime" ? " lifetime" : "";
  return `${sym}${p.amount || 0}${per}`;
};

const AssessmentsPage = () => {
  const navigate = useNavigate();
  // Library mode = SaaS-admin marketplace authoring (instituteId:null on the BE).
  const isLibrary = useLocation().pathname.startsWith("/sa/");
  const base = isLibrary ? "/sa/marketplace/assessments" : "/assessments";
  const viewPath = (id) => (isLibrary ? `${base}/edit/${id}` : `/assessments/${id}`);

  const [assessments, setAssessments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [modeFilter, setModeFilter] = useState("");
  const [sort, setSort] = useState("newest");
  const [filterData, setFilterData] = useState({ statuses: {}, modes: {}, totalAssessments: 0 });
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [toast, setToast] = useState({ show: false, message: "", type: "success" });

  // Version updates (institute side): map of imported assessment _id → update info
  const [updatesMap, setUpdatesMap] = useState({});
  const [applyingId, setApplyingId] = useState(null);

  // Access management (library / SA side): grant institutes access to a paid item
  const [accessTarget, setAccessTarget] = useState(null);
  const [institutes, setInstitutes] = useState([]);
  const [grants, setGrants] = useState([]);
  const [accessLoading, setAccessLoading] = useState(false);
  const [grantInstitute, setGrantInstitute] = useState("");
  const [grantPeriod, setGrantPeriod] = useState("lifetime");

  const fetchFilters = useCallback(async () => {
    try {
      const { data } = await apiClient.get("/assessments/filters");
      setFilterData(data);
    } catch { /* silent */ }
  }, []);

  const fetchAssessments = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        page,
        limit: PAGE_SIZE,
        sort,
        ...(search && { search }),
        ...(statusFilter && { status: statusFilter }),
        ...(modeFilter && { mode: modeFilter }),
      };
      const { data } = await apiClient.get("/assessments", { params });
      setAssessments(data.assessments || []);
      setTotal(data.total || 0);
      setTotalPages(data.totalPages || 1);
    } catch {
      setToast({ show: true, message: "Failed to load assessments", type: "error" });
    }
    setLoading(false);
  }, [page, search, statusFilter, modeFilter, sort]);

  // Imported assessments with a newer source version available (institute only).
  const fetchUpdates = useCallback(async () => {
    if (isLibrary) return;
    try {
      const { data } = await apiClient.get("/assessments/marketplace/updates");
      const map = {};
      (data.updates || []).forEach((u) => { map[u._id] = u; });
      setUpdatesMap(map);
    } catch { /* silent */ }
  }, [isLibrary]);

  useEffect(() => { fetchFilters(); }, [fetchFilters]);
  useEffect(() => { fetchAssessments(); }, [fetchAssessments]);
  useEffect(() => { fetchUpdates(); }, [fetchUpdates]);

  const handleApplyUpdate = async (id) => {
    setApplyingId(id);
    try {
      const { data } = await apiClient.post(`/assessments/${id}/apply-update`);
      setToast({ show: true, message: data.message || "Updated from marketplace", type: "success" });
      fetchAssessments();
      fetchUpdates();
    } catch (err) {
      setToast({ show: true, message: err.response?.data?.message || "Update failed", type: "error" });
    }
    setApplyingId(null);
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await apiClient.delete(`/assessments/${deleteTarget._id}`);
      setToast({ show: true, message: "Assessment deleted", type: "success" });
      fetchAssessments();
      fetchFilters();
    } catch (err) {
      setToast({ show: true, message: err.response?.data?.message || "Failed to delete", type: "error" });
    }
    setDeleteTarget(null);
  };

  const handlePublish = async (id) => {
    try {
      await apiClient.put(`/assessments/${id}/publish`);
      setToast({ show: true, message: "Assessment published!", type: "success" });
      fetchAssessments();
      fetchFilters();
    } catch (err) {
      setToast({ show: true, message: err.response?.data?.message || "Failed to publish", type: "error" });
    }
  };

  const handleDuplicate = async (id) => {
    try {
      await apiClient.post(`/assessments/${id}/duplicate`);
      setToast({ show: true, message: "Assessment duplicated", type: "success" });
      fetchAssessments();
      fetchFilters();
    } catch (err) {
      setToast({ show: true, message: err.response?.data?.message || "Failed to duplicate", type: "error" });
    }
  };

  const handleArchive = async (id) => {
    try {
      await apiClient.put(`/assessments/${id}/archive`);
      setToast({ show: true, message: "Assessment archived", type: "success" });
      fetchAssessments();
      fetchFilters();
    } catch (err) {
      setToast({ show: true, message: err.response?.data?.message || "Failed to archive", type: "error" });
    }
  };

  // ── Marketplace ────────────────────────────────────────────────────
  // Browsing/importing now lives on a dedicated page (/assessments/marketplace).
  const handleToggleMarketplace = async (a) => {
    const publish = a.visibility !== "marketplace";
    try {
      await apiClient.put(`/assessments/${a._id}/publish-marketplace`, { publish });
      setToast({
        show: true,
        message: publish ? "Shared to marketplace" : "Removed from marketplace",
        type: "success",
      });
      fetchAssessments();
    } catch (err) {
      setToast({ show: true, message: err.response?.data?.message || "Failed to update", type: "error" });
    }
  };

  // ── Access management (SA / library side) ──────────────────────────────
  const openAccessModal = async (a) => {
    setAccessTarget(a);
    setGrantInstitute("");
    setGrantPeriod("lifetime");
    setAccessLoading(true);
    try {
      const [instRes, grantRes] = await Promise.all([
        institutes.length ? Promise.resolve({ data: { institutes } }) : apiClient.get("/sa/institutes", { params: { limit: 500 } }),
        apiClient.get("/assessments/entitlements", { params: { assessmentId: a._id } }),
      ]);
      setInstitutes(instRes.data.institutes || instRes.data || []);
      setGrants(grantRes.data.entitlements || []);
    } catch {
      setToast({ show: true, message: "Failed to load access list", type: "error" });
    }
    setAccessLoading(false);
  };

  const handleGrant = async () => {
    if (!grantInstitute || !accessTarget) return;
    try {
      await apiClient.post("/assessments/entitlements", {
        instituteId: grantInstitute,
        assessmentId: accessTarget._id,
        period: grantPeriod,
      });
      setToast({ show: true, message: "Access granted", type: "success" });
      setGrantInstitute("");
      const { data } = await apiClient.get("/assessments/entitlements", { params: { assessmentId: accessTarget._id } });
      setGrants(data.entitlements || []);
    } catch (err) {
      setToast({ show: true, message: err.response?.data?.message || "Failed to grant", type: "error" });
    }
  };

  const handleRevoke = async (entId) => {
    try {
      await apiClient.delete(`/assessments/entitlements/${entId}`);
      setGrants((prev) => prev.map((g) => (g._id === entId ? { ...g, status: "revoked" } : g)));
      setToast({ show: true, message: "Access revoked", type: "success" });
    } catch (err) {
      setToast({ show: true, message: err.response?.data?.message || "Failed to revoke", type: "error" });
    }
  };

  const updateCount = Object.keys(updatesMap).length;

  return (
    <div className="assessments-page">
      <PageHeader
        icon={ClipboardCheck}
        title="Assessments"
        description={`${filterData.totalAssessments || 0} total assessments`}
        breadcrumbs={assessmentsTrail({ isLibrary })}
        actions={
          <div className="d-flex align-items-center gap-2">
            {!isLibrary && (
              <AppButton
                variant="outline"
                className="d-flex align-items-center gap-2"
                onClick={() => navigate("/assessments/marketplace")}
              >
                <Store size={16} /> Marketplace
              </AppButton>
            )}
            {!isLibrary && (
              <AppButton
                variant="outline"
                className="d-flex align-items-center gap-2"
                onClick={() => navigate("/assessments/import")}
              >
                <Upload size={16} /> Import Excel
              </AppButton>
            )}
            <AppButton
              variant="default"
              className="d-flex align-items-center gap-2"
              onClick={() => navigate(`${base}/create`)}
            >
              <Plus size={16} /> Create Assessment
            </AppButton>
          </div>
        }
      />

      {/* Stats */}
      <StatsGrid className="assessments-stats-row">
        <StatsCard title="Total" value={filterData.totalAssessments || 0} icon={ClipboardCheck} iconColor="blue" />
        <StatsCard title="Published" value={filterData.statuses?.published || 0} icon={Send} iconColor="emerald" />
        <StatsCard title="Drafts" value={filterData.statuses?.draft || 0} icon={Pencil} iconColor="amber" />
        <StatsCard title="Archived" value={filterData.statuses?.archived || 0} icon={Archive} iconColor="rose" />
      </StatsGrid>

      {!isLibrary && updateCount > 0 && (
        <div className="assessment-updates-banner">
          <RefreshCw size={16} />
          <span>
            {updateCount} imported {updateCount === 1 ? "assessment has" : "assessments have"} a
            newer version on the marketplace. Open its menu → <strong>Update from marketplace</strong>.
          </span>
        </div>
      )}

      {/* Toolbar */}
      <div className="assessments-toolbar">
        <div className="search-wrapper">
          <SearchInput value={search} onChange={(v) => { setSearch(v); setPage(1); }} placeholder="Search assessments..." />
        </div>
        <div className="filter-wrapper">
          <Form.Select size="sm" value={statusFilter} onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}>
            <option value="">All Statuses</option>
            {Object.entries(STATUS_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </Form.Select>
        </div>
        <div className="filter-wrapper">
          <Form.Select size="sm" value={modeFilter} onChange={(e) => { setModeFilter(e.target.value); setPage(1); }}>
            <option value="">All Modes</option>
            {Object.entries(MODE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </Form.Select>
        </div>
        <div className="filter-wrapper">
          <Form.Select size="sm" value={sort} onChange={(e) => { setSort(e.target.value); setPage(1); }}>
            <option value="newest">Newest</option>
            <option value="oldest">Oldest</option>
            <option value="a-z">A → Z</option>
            <option value="z-a">Z → A</option>
            <option value="most-attempted">Most Attempted</option>
          </Form.Select>
        </div>
      </div>

      {/* List */}
      {loading ? (
        <div className="text-center py-5"><Spinner animation="border" size="sm" /></div>
      ) : assessments.length === 0 ? (
        <EmptyState
          icon={<ClipboardCheck size={40} />}
          title="No assessments yet"
          description={search || statusFilter || modeFilter
            ? "Try adjusting your filters"
            : "Create your first quiz or exam to get started"}
          action={
            !search && !statusFilter ? (
              <AppButton variant="default" onClick={() => navigate(`${base}/create`)}>
                <Plus size={16} className="me-1" /> Create Assessment
              </AppButton>
            ) : null
          }
        />
      ) : (
        <>
          <div className="assessment-list">
            {assessments.map((a) => (
              <div className="assessment-card" key={a._id}>
                <div className="assessment-card__header">
                  <div className="flex-grow-1">
                    <div className="assessment-card__badges">
                      <span className={`assessment-card__status assessment-card__status--${a.status}`}>
                        {STATUS_LABELS[a.status] || a.status}
                      </span>
                      <span className="assessment-card__mode">{MODE_LABELS[a.mode] || a.mode}</span>
                      <span className="assessment-card__selection">
                        {a.selectionMode === "dynamic" ? "Dynamic" : "Manual"}
                      </span>
                      {a.pricing?.model === "paid" && (
                        <span className="assessment-card__price">{fmtPrice(a.pricing)}</span>
                      )}
                      {!isLibrary && updatesMap[a._id] && (
                        <span className="assessment-card__update">
                          <RefreshCw size={11} /> Update available
                        </span>
                      )}
                    </div>
                    <h3
                      className="assessment-card__title"
                      onClick={() => navigate(viewPath(a._id))}
                    >
                      {a.title}
                    </h3>
                    {a.description && (
                      <p className="assessment-card__description">{a.description}</p>
                    )}
                  </div>
                  <div className="assessment-card__actions">
                    <ActionMenu trigger={<MoreVertical size={16} />}>
                      <ActionMenuItem onClick={() => navigate(viewPath(a._id))}>
                        <Eye size={14} /> View
                      </ActionMenuItem>
                      <ActionMenuItem onClick={() => navigate(`${base}/${a._id}/preview`)}>
                        <Play size={14} /> Preview as a student
                      </ActionMenuItem>
                      <ActionMenuItem onClick={() => navigate(`${base}/edit/${a._id}`)}>
                        <Pencil size={14} /> Edit
                      </ActionMenuItem>
                      {!isLibrary && updatesMap[a._id] && (
                        <ActionMenuItem
                          onClick={() => handleApplyUpdate(a._id)}
                          disabled={applyingId === a._id}
                        >
                          <RefreshCw size={14} /> {applyingId === a._id ? "Updating…" : "Update from marketplace"}
                        </ActionMenuItem>
                      )}
                      <ActionMenuItem onClick={() => handleDuplicate(a._id)}>
                        <Copy size={14} /> Duplicate
                      </ActionMenuItem>
                      {a.status === "draft" && (
                        <ActionMenuItem onClick={() => handlePublish(a._id)}>
                          <Send size={14} /> Publish
                        </ActionMenuItem>
                      )}
                      {a.status === "published" && (
                        <ActionMenuItem onClick={() => handleArchive(a._id)}>
                          <Archive size={14} /> Archive
                        </ActionMenuItem>
                      )}
                      {a.status === "published" && (
                        <ActionMenuItem onClick={() => handleToggleMarketplace(a)}>
                          <Store size={14} /> {a.visibility === "marketplace" ? "Remove from marketplace" : "Share to marketplace"}
                        </ActionMenuItem>
                      )}
                      {isLibrary && a.visibility === "marketplace" && a.pricing?.model === "paid" && (
                        <ActionMenuItem onClick={() => openAccessModal(a)}>
                          <KeyRound size={14} /> Manage access
                        </ActionMenuItem>
                      )}
                      {a.totalAttempts === 0 && (
                        <>
                          <ActionMenuDivider />
                          <ActionMenuItem danger onClick={() => setDeleteTarget(a)}>
                            <Trash2 size={14} /> Delete
                          </ActionMenuItem>
                        </>
                      )}
                    </ActionMenu>
                  </div>
                </div>

                <div className="assessment-card__meta">
                  <span className="assessment-card__meta-item">
                    <HelpCircle size={14} /> {a.questionCount || 0} questions
                  </span>
                  {a.timeLimit > 0 && (
                    <span className="assessment-card__meta-item">
                      <Clock size={14} /> {a.timeLimit} min
                    </span>
                  )}
                  {a.passingPercentage > 0 && (
                    <span className="assessment-card__meta-item">
                      Pass: {a.passingPercentage}%
                    </span>
                  )}
                  <span className="assessment-card__meta-item">
                    <BarChart3 size={14} /> {a.totalAttempts} attempts
                  </span>
                  {a.assignmentType === "public" && (
                    <span className="assessment-card__meta-item">
                      <Globe size={14} /> Public exams only
                    </span>
                  )}
                  {a.assignedBatches?.length > 0 && (
                    <span className="assessment-card__meta-item">
                      <Users size={14} /> {a.assignedBatches.map(b => b.name || b).join(", ")}
                    </span>
                  )}
                  {a.startDate && (
                    <span className="assessment-card__meta-item">
                      <Clock size={14} /> {new Date(a.startDate).toLocaleDateString()}
                      {a.endDate && ` – ${new Date(a.endDate).toLocaleDateString()}`}
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <Paginator
              page={page}
              totalPages={totalPages}
              total={total}
              pageSize={PAGE_SIZE}
              onChange={setPage}
            />
          )}
        </>
      )}

      <ConfirmModal
        show={!!deleteTarget}
        onHide={() => setDeleteTarget(null)}
        onConfirm={handleDelete}
        title="Delete Assessment"
        message="Are you sure you want to delete this assessment? This action cannot be undone."
        confirmText="Delete"
        variant="danger"
      />

      <AppModal show={!!accessTarget} onHide={() => setAccessTarget(null)} title="Manage Access" size="lg">
        {accessTarget && (
          <>
            <p className="text-muted" style={{ fontSize: "0.85rem" }}>
              Grant institutes access to <strong>{accessTarget.title}</strong>
              {" "}({fmtPrice(accessTarget.pricing) || "free"}). Paid assessments can only be
              imported by institutes you grant here.
            </p>
            {accessLoading ? (
              <div className="text-center py-4"><Spinner animation="border" size="sm" /></div>
            ) : (
              <>
                <div className="access-grant-row">
                  <Form.Select size="sm" value={grantInstitute} onChange={(e) => setGrantInstitute(e.target.value)}>
                    <option value="">Select institute…</option>
                    {institutes.map((i) => (
                      <option key={i._id} value={i._id}>
                        {i.name}{i.subdomain ? ` (${i.subdomain})` : ""}
                      </option>
                    ))}
                  </Form.Select>
                  <Form.Select size="sm" value={grantPeriod} onChange={(e) => setGrantPeriod(e.target.value)} style={{ maxWidth: 150 }}>
                    <option value="lifetime">Lifetime</option>
                    <option value="monthly">Monthly</option>
                    <option value="yearly">Yearly</option>
                  </Form.Select>
                  <AppButton variant="default" size="sm" disabled={!grantInstitute} onClick={handleGrant}>
                    <KeyRound size={14} /> Grant
                  </AppButton>
                </div>

                <div className="access-grant-list">
                  {grants.filter((g) => g.status === "active").length === 0 ? (
                    <p className="text-muted" style={{ fontSize: "0.82rem", margin: "0.75rem 0 0" }}>
                      No institutes have access yet.
                    </p>
                  ) : (
                    grants.filter((g) => g.status === "active").map((g) => (
                      <div className="access-grant-item" key={g._id}>
                        <div>
                          <div className="access-grant-name">{g.instituteId?.name || "Institute"}</div>
                          <div className="access-grant-meta">
                            {g.period || "lifetime"}
                            {g.expiresAt ? ` · until ${new Date(g.expiresAt).toLocaleDateString()}` : ""}
                          </div>
                        </div>
                        <AppButton variant="ghost" size="sm" onClick={() => handleRevoke(g._id)}>
                          <Trash2 size={14} /> Revoke
                        </AppButton>
                      </div>
                    ))
                  )}
                </div>
              </>
            )}
          </>
        )}
      </AppModal>

      <CommonToaster show={toast.show} message={toast.message} type={toast.type} onClose={() => setToast({ ...toast, show: false })} />
    </div>
  );
};

export default AssessmentsPage;
